import React, { useState } from 'react';
import {
    BookOpen,
    Search,
    FileText,
    DollarSign,
    Key,
    Shield,
    CheckCircle,
    AlertCircle,
    Home,
    ArrowLeft,
    MapPin,
    Clock,
    Users,
    Calculator,
    Eye,
    Camera,
    Phone,
    Mail,
    Building,
    Car,
    Wifi,
    Zap,
    Droplets,
    Trash2,
    Bed,
    Bath,
    Square,
    Star,
    TrendingUp,
    ChevronRight,
    Download,
    ExternalLink, Scale
} from 'lucide-react';

const RentalGuide = () => {
    const [activeStep, setActiveStep] = useState(1);
    const [selectedCategory, setSelectedCategory] = useState('first-time');

    const rentalSteps = [
        {
            id: 1,
            title: 'Search & Research',
            icon: Search,
            color: 'blue',
            duration: '1-2 weeks',
            description: 'Find properties that match your needs and budget',
            tasks: [
                'Define your budget and requirements',
                'Research neighborhoods',
                'Use Rent Utopia to browse listings',
                'Create a shortlist of properties',
                'Check property reviews and ratings'
            ]
        },
        {
            id: 2,
            title: 'Property Viewing',
            icon: Eye,
            color: 'green',
            duration: '1 week',
            description: 'Visit properties and evaluate them thoroughly',
            tasks: [
                'Schedule property viewings',
                'Inspect property condition',
                'Check amenities and facilities',
                'Evaluate neighborhood safety',
                'Take photos and notes'
            ]
        },
        {
            id: 3,
            title: 'Application Process',
            icon: FileText,
            color: 'purple',
            duration: '3-5 days',
            description: 'Submit your rental application with required documents',
            tasks: [
                'Complete rental application form',
                'Provide identification documents',
                'Submit employment verification',
                'Include references and guarantors',
                'Pay application processing fee'
            ]
        },
        {
            id: 4,
            title: 'Background Check',
            icon: Shield,
            color: 'yellow',
            duration: '2-3 days',
            description: 'Landlord verifies your information and creditworthiness',
            tasks: [
                'Credit history verification',
                'Employment confirmation',
                'Reference checks',
                'Previous landlord verification',
                'Income verification'
            ]
        },
        {
            id: 5,
            title: 'Lease Agreement',
            icon: FileText,
            color: 'indigo',
            duration: '1-2 days',
            description: 'Review and sign the rental agreement',
            tasks: [
                'Review lease terms carefully',
                'Negotiate any necessary changes',
                'Understand rental policies',
                'Sign lease agreement',
                'Get copies of all documents'
            ]
        },
        {
            id: 6,
            title: 'Move-in Process',
            icon: Key,
            color: 'green',
            duration: '1 day',
            description: 'Complete payments and receive keys',
            tasks: [
                'Pay security deposit',
                'Pay first month rent',
                'Complete property inspection',
                'Receive keys and access cards',
                'Set up utilities and services'
            ]
        }
    ];

    const budgetGuide = {
        monthly_income: [
            { range: 'KES 30,000 - 50,000', recommended: 'KES 10,000 - 15,000', type: 'Studio/1BR' },
            { range: 'KES 50,000 - 80,000', recommended: 'KES 15,000 - 25,000', type: '1BR/2BR' },
            { range: 'KES 80,000 - 120,000', recommended: 'KES 25,000 - 35,000', type: '2BR/3BR' },
            { range: 'KES 120,000+', recommended: 'KES 35,000+', type: '3BR+/House' }
        ],
        additional_costs: [
            { item: 'Security Deposit', amount: '1-2 months rent', when: 'Before move-in' },
            { item: 'Electricity Deposit', amount: 'KES 3,000 - 10,000', when: 'Before move-in' },
            { item: 'Water Deposit', amount: 'KES 2,000 - 5,000', when: 'Before move-in' },
            { item: 'Agent Commission', amount: '1 month rent', when: 'Upon signing lease' },
            { item: 'Legal Fees', amount: 'KES 5,000 - 15,000', when: 'During lease signing' }
        ]
    };

    const neighborhoods = [
        {
            name: 'Westlands',
            price_range: 'KES 25,000 - 80,000',
            characteristics: ['Business district', 'Restaurants & malls', 'Good transport'],
            best_for: 'Young professionals'
        },
        {
            name: 'Kilimani',
            price_range: 'KES 20,000 - 70,000',
            characteristics: ['Trendy area', 'Nightlife', 'Modern apartments'],
            best_for: 'Young professionals & students'
        },
        {
            name: 'Karen',
            price_range: 'KES 40,000 - 150,000',
            characteristics: ['Upmarket', 'Spacious houses', 'Quiet residential'],
            best_for: 'Families'
        },
        {
            name: 'Kasarani',
            price_range: 'KES 15,000 - 45,000',
            characteristics: ['Affordable', 'Growing area', 'Good schools'],
            best_for: 'First-time renters & families'
        }
    ];

    const requiredDocuments = [
        {
            category: 'Identification',
            items: [
                'National ID or Passport (copy)',
                'KRA PIN Certificate',
                'Passport-size photos (2)'
            ]
        },
        {
            category: 'Employment',
            items: [
                'Employment letter',
                'Recent pay slips (3 months)',
                'Bank statements (3-6 months)',
                'Tax compliance certificate'
            ]
        },
        {
            category: 'References',
            items: [
                'Previous landlord reference',
                'Character references (2)',
                'Guarantor information',
                'Emergency contact details'
            ]
        }
    ];

    const StepCard = ({ step, isActive }) => {
        const Icon = step.icon;

        return (
            <div
                className={`p-6 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                    isActive
                        ? 'border-highland-500 bg-highland-50'
                        : 'border-soil-100 bg-white hover:border-highland-400'
                }`}
                onClick={() => setActiveStep(step.id)}
            >
                <div className="flex items-start space-x-4">
                    <div className={`p-3 rounded-lg ${isActive ? 'bg-white' : 'bg-highland-50'}`}>
                        <Icon className="w-6 h-6 text-highland-500" />
                    </div>
                    <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="font-semibold text-soil-900">{step.title}</h3>
                            <span className="text-sm text-soil-400">{step.duration}</span>
                        </div>
                        <p className="text-soil-600 text-sm mb-3">{step.description}</p>

                        {isActive && (
                            <div className="space-y-2">
                                <h4 className="font-medium text-soil-900 text-sm">Key Tasks:</h4>
                                <ul className="space-y-1">
                                    {step.tasks.map((task, index) => (
                                        <li key={index} className="flex items-start space-x-2 text-sm">
                                            <CheckCircle className="w-4 h-4 text-highland-500 mt-0.5 flex-shrink-0" />
                                            <span className="text-soil-600">{task}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const CategoryCard = ({ id, title, description, icon: Icon, isActive, onClick }) => (
        <button
            onClick={() => onClick(id)}
            className={`p-6 text-left rounded-xl border-2 transition-all duration-200 ${
                isActive
                    ? 'border-highland-500 bg-highland-50'
                    : 'border-soil-100 bg-white hover:border-highland-400'
            }`}
        >
            <div className="flex items-start space-x-4">
                <div className="p-3 bg-highland-50 rounded-lg">
                    <Icon className="w-6 h-6 text-highland-500" />
                </div>
                <div>
                    <h3 className="font-semibold text-soil-900 mb-2">{title}</h3>
                    <p className="text-soil-600 text-sm">{description}</p>
                </div>
            </div>
        </button>
    );

    return (
        <div className="min-h-screen bg-karibu-100">
            <div style={{paddingTop: '5rem'}}></div>
            {/* Header */}
            <div className="relative bg-highland-700 text-karibu-100 overflow-hidden">
                <div
                    aria-hidden="true"
                    className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[420px] rounded-arch bg-gradient-to-b from-dawn-500/20 via-highland-500/10 to-transparent blur-2xl"
                />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    <div className="text-center">
                        <div className="flex items-center justify-center mb-4">
                            <div className="p-3 bg-white/10 rounded-full">
                                <BookOpen className="w-8 h-8"/>
                            </div>
                        </div>
                        <h1 className="font-display text-4xl font-bold mb-4">Complete Rental Guide</h1>
                        <p className="text-xl text-karibu-200/80 max-w-3xl mx-auto">
                            Everything you need to know about renting in Kenya. From finding the perfect property
                            to signing your lease agreement.
                        </p>
                    </div>
                </div>
            </div>

            {/* Navigation */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                <div className="flex items-center space-x-2 text-sm text-soil-600">
                    <button
                        onClick={() => window.history.back()}
                        className="flex items-center hover:text-highland-500 transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4 mr-1"/>
                        Back
                    </button>
                    <span>/</span>
                    <button
                        onClick={() => window.location.href = '/'}
                        className="flex items-center hover:text-highland-500 transition-colors"
                    >
                        <Home className="w-4 h-4 mr-1"/>
                        Home
                    </button>
                    <span>/</span>
                    <span className="text-soil-900">Rental Guide</span>
                </div>
            </div>

            {/* Quick Navigation */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="grid md:grid-cols-3 gap-6 mb-12">
                    <CategoryCard
                        id="first-time"
                        title="First-Time Renters"
                        description="New to renting? Start here for the basics."
                        icon={Users}
                        isActive={selectedCategory === 'first-time'}
                        onClick={setSelectedCategory}
                    />
                    <CategoryCard
                        id="budget"
                        title="Budget Planning"
                        description="Calculate costs and plan your rental budget."
                        icon={Calculator}
                        isActive={selectedCategory === 'budget'}
                        onClick={setSelectedCategory}
                    />
                    <CategoryCard
                        id="neighborhoods"
                        title="Neighborhood Guide"
                        description="Explore different areas and their characteristics."
                        icon={MapPin}
                        isActive={selectedCategory === 'neighborhoods'}
                        onClick={setSelectedCategory}
                    />
                </div>

                {/* Content based on selected category */}
                {selectedCategory === 'first-time' && (
                    <div className="space-y-12">
                        {/* Rental Process Steps */}
                        <section>
                            <div className="text-center mb-8">
                                <h2 className="font-display text-3xl font-bold text-soil-900 mb-4">6-Step Rental Process</h2>
                                <p className="text-soil-600 max-w-2xl mx-auto">
                                    Follow this step-by-step guide to navigate the rental process smoothly and
                                    efficiently.
                                </p>
                            </div>

                            <div className="grid lg:grid-cols-2 gap-8">
                                <div className="space-y-4">
                                    {rentalSteps.map((step) => (
                                        <StepCard
                                            key={step.id}
                                            step={step}
                                            isActive={activeStep === step.id}
                                        />
                                    ))}
                                </div>

                                <div className="lg:sticky lg:top-8">
                                    <div className="bg-white rounded-xl shadow-sm p-8">
                                        <h3 className="text-xl font-semibold text-soil-900 mb-6">Required Documents</h3>
                                        <div className="space-y-6">
                                            {requiredDocuments.map((category, index) => (
                                                <div key={index}>
                                                    <h4 className="font-medium text-soil-900 mb-3">{category.category}</h4>
                                                    <ul className="space-y-2">
                                                        {category.items.map((item, itemIndex) => (
                                                            <li key={itemIndex}
                                                                className="flex items-center space-x-2 text-sm">
                                                                <CheckCircle className="w-4 h-4 text-highland-500"/>
                                                                <span className="text-soil-600">{item}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            ))}
                                        </div>

                                        <div className="mt-6 p-4 bg-highland-50 rounded-lg">
                                            <div className="flex items-start space-x-3">
                                                <AlertCircle className="w-5 h-5 text-highland-500 mt-0.5"/>
                                                <div>
                                                    <h4 className="font-medium text-highland-700">Pro Tip</h4>
                                                    <p className="text-highland-600 text-sm">
                                                        Prepare all documents in advance to speed up your application
                                                        process.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Red Flags Section */}
                        <section className="bg-white rounded-xl shadow-sm p-8">
                            <h2 className="font-display text-2xl font-bold text-soil-900 mb-6">Red Flags to Watch Out For</h2>
                            <div className="grid md:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    <h3 className="font-semibold text-murram-600">🚨 Avoid These Properties</h3>
                                    <ul className="space-y-2">
                                        {[
                                            'Unusually low rent for the area',
                                            'Landlord refuses property viewing',
                                            'Requests for large upfront payments',
                                            'No proper documentation',
                                            'Poor property maintenance',
                                            'Unclear lease terms'
                                        ].map((flag, index) => (
                                            <li key={index} className="flex items-start space-x-2">
                                                <AlertCircle className="w-4 h-4 text-murram-500 mt-0.5"/>
                                                <span className="text-soil-600 text-sm">{flag}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <div className="space-y-4">
                                    <h3 className="font-semibold text-highland-700">✅ Good Signs</h3>
                                    <ul className="space-y-2">
                                        {[
                                            'Professional property management',
                                            'Clear rental agreement',
                                            'Good property condition',
                                            'Responsive communication',
                                            'Positive tenant reviews',
                                            'Transparent pricing'
                                        ].map((sign, index) => (
                                            <li key={index} className="flex items-start space-x-2">
                                                <CheckCircle className="w-4 h-4 text-highland-500 mt-0.5"/>
                                                <span className="text-soil-600 text-sm">{sign}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </section>
                    </div>
                )}

                {selectedCategory === 'budget' && (
                    <div className="space-y-8">
                        {/* Budget Calculator */}
                        <section className="bg-white rounded-xl shadow-sm p-8">
                            <h2 className="font-display text-2xl font-bold text-soil-900 mb-6">Budget Planning Guide</h2>

                            <div className="grid lg:grid-cols-2 gap-8">
                                <div>
                                    <h3 className="text-lg font-semibold text-soil-900 mb-4">Recommended Rent by
                                        Income</h3>
                                    <div className="space-y-3">
                                        {budgetGuide.monthly_income.map((guide, index) => (
                                            <div key={index}
                                                 className="flex items-center justify-between p-4 bg-soil-50 rounded-lg">
                                                <div>
                                                    <p className="font-medium text-soil-900">{guide.range}</p>
                                                    <p className="text-sm text-soil-600">{guide.type}</p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="font-semibold text-highland-500">{guide.recommended}</p>
                                                    <p className="text-xs text-soil-400">monthly rent</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <h3 className="text-lg font-semibold text-soil-900 mb-4">Additional Costs</h3>
                                    <div className="space-y-3">
                                        {budgetGuide.additional_costs.map((cost, index) => (
                                            <div key={index}
                                                 className="flex items-center justify-between p-4 bg-dawn-100/40 rounded-lg">
                                                <div>
                                                    <p className="font-medium text-soil-900">{cost.item}</p>
                                                    <p className="text-sm text-soil-600">{cost.when}</p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="font-semibold text-dawn-600">{cost.amount}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="mt-8 p-6 bg-highland-50 rounded-lg">
                                <h4 className="font-semibold text-highland-700 mb-2">💡 Budget Tip</h4>
                                <p className="text-highland-600">
                                    The 30% rule: Your rent should not exceed 30% of your gross monthly income.
                                    This ensures you have enough left for other expenses and savings.
                                </p>
                            </div>
                        </section>

                        {/* Cost Breakdown */}
                        <section className="bg-white rounded-xl shadow-sm p-8">
                            <h2 className="font-display text-2xl font-bold text-soil-900 mb-6">Move-in Cost Calculator</h2>
                            <p className="text-soil-600 mb-6">
                                Use this calculator to estimate your total move-in costs for different rent ranges.
                            </p>

                            <div className="grid md:grid-cols-3 gap-6">
                                {[
                                    {rent: 25000, type: 'Studio/1BR'},
                                    {rent: 50000, type: '2BR Apartment'},
                                    {rent: 80000, type: '3BR Apartment'}
                                ].map((example, index) => (
                                    <div key={index} className="p-6 border border-soil-100 rounded-lg">
                                        <h3 className="font-semibold text-soil-900 mb-4">
                                            KES {example.rent.toLocaleString()}/month
                                        </h3>
                                        <p className="text-sm text-soil-600 mb-4">{example.type}</p>

                                        <div className="space-y-2 text-sm">
                                            <div className="flex justify-between">
                                                <span>First month rent</span>
                                                <span>KES {example.rent.toLocaleString()}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>Security deposit</span>
                                                <span>KES {(example.rent * 2).toLocaleString()}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>Agent commission</span>
                                                <span>KES {example.rent.toLocaleString()}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>Utility deposits</span>
                                                <span>KES 8,000</span>
                                            </div>
                                            <div className="border-t pt-2 flex justify-between font-semibold">
                                                <span>Total move-in cost</span>
                                                <span>KES {(example.rent * 4 + 8000).toLocaleString()}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    </div>
                )}

                {selectedCategory === 'neighborhoods' && (
                    <div className="space-y-8">
                        {/* Neighborhood Comparison */}
                        <section className="bg-white rounded-xl shadow-sm p-8">
                            <h2 className="font-display text-2xl font-bold text-soil-900 mb-6">Popular Neighborhoods in Nairobi</h2>

                            <div className="grid gap-6">
                                {neighborhoods.map((neighborhood, index) => (
                                    <div key={index}
                                         className="p-6 border border-soil-100 rounded-lg hover:shadow-md transition-shadow">
                                        <div className="flex items-start justify-between mb-4">
                                            <div>
                                                <h3 className="text-xl font-semibold text-soil-900">{neighborhood.name}</h3>
                                                <p className="text-lg font-medium text-highland-500">{neighborhood.price_range}</p>
                                            </div>
                                            <span className="px-3 py-1 bg-highland-50 text-highland-600 text-sm rounded-full">
                                                {neighborhood.best_for}
                                            </span>
                                        </div>

                                        <div className="flex flex-wrap gap-2">
                                            {neighborhood.characteristics.map((char, charIndex) => (
                                                <span key={charIndex}
                                                      className="px-3 py-1 bg-soil-100 text-soil-600 text-sm rounded-full">
                                                    {char}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>

                        {/* Factors to Consider */}
                        <section className="bg-white rounded-xl shadow-sm p-8">
                            <h2 className="font-display text-2xl font-bold text-soil-900 mb-6">Choosing the Right Neighborhood</h2>

                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {[
                                    {
                                        title: 'Commute & Transport',
                                        icon: Car,
                                        items: [
                                            'Distance to work',
                                            'Public transport access',
                                            'Traffic patterns',
                                            'Parking availability'
                                        ]
                                    },
                                    {
                                        title: 'Amenities & Services',
                                        icon: Building,
                                        items: [
                                            'Shopping centers',
                                            'Hospitals & clinics',
                                            'Schools & universities',
                                            'Banks & ATMs'
                                        ]
                                    },
                                    {
                                        title: 'Safety & Security',
                                        icon: Shield,
                                        items: [
                                            'Crime rates',
                                            'Street lighting',
                                            'Security presence',
                                            'Gated communities'
                                        ]
                                    },
                                    {
                                        title: 'Utilities & Infrastructure',
                                        icon: Zap,
                                        items: [
                                            'Reliable electricity',
                                            'Water supply',
                                            'Internet connectivity',
                                            'Waste management'
                                        ]
                                    },
                                    {
                                        title: 'Lifestyle & Recreation',
                                        icon: Star,
                                        items: [
                                            'Restaurants & cafes',
                                            'Gyms & sports facilities',
                                            'Parks & recreation',
                                            'Entertainment venues'
                                        ]
                                    },
                                    {
                                        title: 'Future Development',
                                        icon: TrendingUp,
                                        items: [
                                            'Planned infrastructure',
                                            'Property value trends',
                                            'New developments',
                                            'Investment potential'
                                        ]
                                    }
                                ].map((factor, index) => (
                                    <div key={index} className="p-6 bg-soil-50 rounded-lg">
                                        <div className="flex items-center space-x-3 mb-4">
                                            <factor.icon className="w-6 h-6 text-highland-500"/>
                                            <h3 className="font-semibold text-soil-900">{factor.title}</h3>
                                        </div>
                                        <ul className="space-y-2">
                                            {factor.items.map((item, itemIndex) => (
                                                <li key={itemIndex} className="flex items-center space-x-2">
                                                    <CheckCircle className="w-4 h-4 text-highland-500"/>
                                                    <span className="text-soil-600 text-sm">{item}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        </section>
                    </div>
                )}

                {/* Resources Section */}
                <section className="bg-white rounded-xl shadow-sm p-8">
                    <h2 className="font-display text-2xl font-bold text-soil-900 mb-6">Additional Resources</h2>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[
                            {
                                title: 'Rental Agreement Template',
                                description: 'Download a standard rental agreement template',
                                icon: Download,
                                action: 'Download PDF'
                            },
                            {
                                title: 'Property Inspection Checklist',
                                description: 'Comprehensive checklist for property viewing',
                                icon: CheckCircle,
                                action: 'View Checklist'
                            },
                            {
                                title: 'Tenant Rights Guide',
                                description: 'Know your rights as a tenant in Kenya',
                                icon: Shield,
                                action: 'Learn More'
                            },
                            {
                                title: 'Legal Support',
                                description: 'Find legal assistance for rental disputes',
                                icon: Scale,
                                action: 'Find Lawyers'
                            },
                            {
                                title: 'Moving Services',
                                description: 'Professional moving companies in Nairobi',
                                icon: Home,
                                action: 'View Services'
                            },
                            {
                                title: 'Insurance Options',
                                description: 'Protect your belongings with rental insurance',
                                icon: Shield,
                                action: 'Get Quote'
                            }
                        ].map((resource, index) => (
                            <div key={index}
                                 className="p-6 border border-soil-100 rounded-lg hover:shadow-md transition-shadow">
                                <div className="flex items-start space-x-3 mb-4">
                                    <resource.icon className="w-6 h-6 text-highland-500"/>
                                    <div>
                                        <h3 className="font-semibold text-soil-900">{resource.title}</h3>
                                        <p className="text-soil-600 text-sm">{resource.description}</p>
                                    </div>
                                </div>
                                <button
                                    className="flex items-center text-highland-500 hover:text-highland-600 text-sm font-medium">
                                    {resource.action}
                                    <ExternalLink className="w-4 h-4 ml-1"/>
                                </button>
                            </div>
                        ))}
                    </div>
                </section>

                {/* FAQ Section */}
                <section className="bg-white rounded-xl shadow-sm p-8">
                    <h2 className="font-display text-2xl font-bold text-soil-900 mb-6">Frequently Asked Questions</h2>

                    <div className="grid md:grid-cols-2 gap-8">
                        <div className="space-y-6">
                            <div>
                                <h3 className="font-semibold text-soil-900 mb-2">How much should I budget for rent?</h3>
                                <p className="text-soil-600 text-sm">
                                    Follow the 30% rule - your rent should not exceed 30% of your gross monthly income.
                                    This ensures you have enough for other expenses and savings.
                                </p>
                            </div>

                            <div>
                                <h3 className="font-semibold text-soil-900 mb-2">What documents do I need to apply?</h3>
                                <p className="text-soil-600 text-sm">
                                    You'll need ID/passport, KRA PIN, employment letter, pay slips, bank statements,
                                    and references. Some landlords may require additional documents.
                                </p>
                            </div>

                            <div>
                                <h3 className="font-semibold text-soil-900 mb-2">How long does the application process
                                    take?</h3>
                                <p className="text-soil-600 text-sm">
                                    Typically 3-7 days from application submission to approval, depending on the
                                    landlord's verification process and completeness of your documents.
                                </p>
                            </div>

                            <div>
                                <h3 className="font-semibold text-soil-900 mb-2">Can I negotiate the rent?</h3>
                                <p className="text-soil-600 text-sm">
                                    Yes, especially in a competitive market or if you're a long-term tenant.
                                    Be prepared to justify your request with market research or improvements you'll
                                    make.
                                </p>
                            </div>
                        </div>

                        <div className="space-y-6">
                            <div>
                                <h3 className="font-semibold text-soil-900 mb-2">What should I look for during property
                                    viewing?</h3>
                                <p className="text-soil-600 text-sm">
                                    Check water pressure, electrical outlets, security features, natural lighting,
                                    storage space, and overall maintenance. Don't forget to test WiFi if available.
                                </p>
                            </div>

                            <div>
                                <h3 className="font-semibold text-soil-900 mb-2">Are utility deposits refundable?</h3>
                                <p className="text-soil-600 text-sm">
                                    Yes, utility deposits are typically refundable when you move out, minus any
                                    outstanding bills. Keep all receipts and payment records.
                                </p>
                            </div>

                            <div>
                                <h3 className="font-semibold text-soil-900 mb-2">What if I need to break my lease
                                    early?</h3>
                                <p className="text-soil-600 text-sm">
                                    Check your lease agreement for early termination clauses. You may need to give
                                    notice (usually 1-3 months) and potentially forfeit your security deposit.
                                </p>
                            </div>

                            <div>
                                <h3 className="font-semibold text-soil-900 mb-2">Should I use Rent Utopia for my rental
                                    search?</h3>
                                <p className="text-soil-600 text-sm">
                                    Rent Utopia provides verified listings, secure application processing, and
                                    protection for both tenants and landlords. It's a safer, more efficient way to rent.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Call to Action */}
                <section className="relative bg-highland-700 rounded-xl text-karibu-100 p-8 text-center overflow-hidden">
                    <div
                        aria-hidden="true"
                        className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[280px] rounded-arch bg-gradient-to-b from-dawn-500/20 to-transparent blur-2xl"
                    />
                    <h2 className="relative font-display text-2xl font-bold mb-4">Ready to Start Your Rental Journey?</h2>
                    <p className="relative text-karibu-200/80 mb-6 max-w-2xl mx-auto">
                        Use Rent Utopia's platform to find verified properties, apply securely, and rent with confidence.
                        Join thousands of satisfied tenants who found their perfect home.
                    </p>
                    <div className="relative flex flex-col sm:flex-row gap-4 justify-center">
                        <button
                            onClick={() => window.location.href = '/listings'}
                            className="px-8 py-3 bg-karibu-100 text-highland-700 font-semibold rounded-lg hover:bg-white transition-colors inline-flex items-center justify-center"
                        >
                            <Search className="w-5 h-5 mr-2"/>
                            Browse Properties
                        </button>
                        <button
                            onClick={() => window.location.href = '/register'}
                            className="px-8 py-3 border-2 border-karibu-100 text-karibu-100 font-semibold rounded-lg hover:bg-karibu-100 hover:text-highland-700 transition-colors inline-flex items-center justify-center"
                        >
                            <Users className="w-5 h-5 mr-2"/>
                            Create Account
                        </button>
                    </div>
                </section>

                {/* Contact Support */}
                <section className="bg-white rounded-xl shadow-sm p-8">
                    <div className="text-center">
                        <h2 className="font-display text-2xl font-bold text-soil-900 mb-4">Need More Help?</h2>
                        <p className="text-soil-600 mb-6">
                            Our rental experts are here to help you navigate the process and answer any questions.
                        </p>

                        <div className="grid md:grid-cols-3 gap-6">
                            <div className="p-6 bg-highland-50 rounded-lg">
                                <Phone className="w-8 h-8 text-highland-500 mx-auto mb-3"/>
                                <h3 className="font-semibold text-soil-900 mb-2">Call Us</h3>
                                <p className="text-soil-600 text-sm mb-3">
                                    Speak with our rental experts
                                </p>
                                <button className="text-highland-500 font-medium hover:text-highland-600">
                                    +254 712 292 899
                                </button>
                            </div>

                            <div className="p-6 bg-dawn-100/40 rounded-lg">
                                <Mail className="w-8 h-8 text-dawn-600 mx-auto mb-3"/>
                                <h3 className="font-semibold text-soil-900 mb-2">Email Support</h3>
                                <p className="text-soil-600 text-sm mb-3">
                                    Get detailed answers via email
                                </p>
                                <button className="text-dawn-600 font-medium hover:opacity-80">
                                    itorama254@gmail.com
                                </button>
                            </div>

                            <div className="p-6 bg-jacaranda-100 rounded-lg">
                                <Building className="w-8 h-8 text-jacaranda-500 mx-auto mb-3"/>
                                <h3 className="font-semibold text-soil-900 mb-2">Visit Office</h3>
                                <p className="text-soil-600 text-sm mb-3">
                                    Meet us in person for guidance
                                </p>
                                <button className="text-jacaranda-600 font-medium hover:opacity-80">
                                    Westlands, Nairobi
                                </button>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default RentalGuide;